import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'

import App from './App.vue'
import router from './router'
import { getSiteConfig } from './config/site.config'

// Import global styles
import './styles/main.css'

// Apply theme and site configuration
const applyThemeConfiguration = () => {
  const config = getSiteConfig();
  const root = document.documentElement;

  // Set CSS variables from config
  root.style.setProperty('--color-primary', config.theme.colors.primary);
  root.style.setProperty('--color-primary-light', config.theme.colors.primaryLight);
  root.style.setProperty('--color-primary-dark', config.theme.colors.primaryDark);
  root.style.setProperty('--color-secondary', config.theme.colors.secondary);
  root.style.setProperty('--color-accent', config.theme.colors.accent);
  root.style.setProperty('--color-recovery', config.theme.colors.recovery);

  root.style.setProperty('--bg-primary', config.theme.backgrounds.primary);
  root.style.setProperty('--bg-secondary', config.theme.backgrounds.secondary);
  root.style.setProperty('--bg-dark', config.theme.backgrounds.dark);
  root.style.setProperty('--bg-gradient', config.theme.backgrounds.gradient);

  root.style.setProperty('--text-primary', config.theme.text.primary);
  root.style.setProperty('--text-secondary', config.theme.text.secondary);
  root.style.setProperty('--text-light', config.theme.text.light);
  root.style.setProperty('--text-muted', config.theme.text.muted);

  root.style.setProperty('--font-sans', config.theme.fonts.sans);
  root.style.setProperty('--font-heading', config.theme.fonts.heading);

  // Update document title
  document.title = config.fullTitle;

  // Update or create meta tags
  const updateMetaTag = (selector: string, attribute: string, value: string) => {
    let meta = document.querySelector(selector);
    if (meta) {
      meta.setAttribute(attribute, value);
    } else {
      // Create the meta tag if it doesn't exist
      meta = document.createElement('meta');
      const attrs = selector.match(/\[([^=]+)="([^"]+)"\]/);
      if (attrs) {
        meta.setAttribute(attrs[1], attrs[2]);
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
      }
    }
  };

  // Update primary meta tags
  updateMetaTag('meta[name="title"]', 'content', config.fullTitle);
  updateMetaTag('meta[name="description"]', 'content', config.meta.description);
  updateMetaTag('meta[name="keywords"]', 'content', config.meta.keywords.join(', '));
  updateMetaTag('meta[name="theme-color"]', 'content', config.theme.colors.primary);

  // Update Open Graph meta tags
  updateMetaTag('meta[property="og:title"]', 'content', config.fullTitle);
  updateMetaTag('meta[property="og:description"]', 'content', config.meta.description);
  updateMetaTag('meta[property="og:image"]', 'content', config.meta.ogImage);
  updateMetaTag('meta[property="og:site_name"]', 'content', config.siteName);

  // Update Twitter meta tags
  updateMetaTag('meta[property="twitter:title"]', 'content', config.fullTitle);
  updateMetaTag('meta[property="twitter:description"]', 'content', config.meta.description);
  updateMetaTag('meta[property="twitter:image"]', 'content', config.meta.ogImage);

  // Update favicon
  const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
  if (favicon) {
    favicon.href = config.meta.favicon;
  }

  // Add preconnect links for API if not already present
  const apiUrl = new URL(config.api.base);
  const apiOrigin = apiUrl.origin;

  if (!document.querySelector(`link[href="${apiOrigin}"]`)) {
    // Add preconnect
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = apiOrigin;
    preconnect.setAttribute('crossorigin', '');
    document.head.appendChild(preconnect);

    // Add dns-prefetch as fallback
    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = apiOrigin;
    document.head.appendChild(dnsPrefetch);
  }
};

// Apply theme on app initialization
applyThemeConfiguration();

const app = createApp(App)
const pinia = createPinia()

// Use plugins
app.use(pinia)
app.use(router)
app.use(ElementPlus)

// Register Element Plus icons
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

// Provide site config globally
app.provide('siteConfig', getSiteConfig())

// Mount the app
app.mount('#app')